import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DEFAULT_LANGUAGE, isSupportedLanguage, isRtl, type LanguageCode } from "./languages";
import { COUNTRY_TO_LANGUAGE } from "./countryToLanguage";
import { detectCountryFromIp } from "./detectCountry";
import { translations } from "./locales";
import type { TranslationKey } from "./types";

const STORAGE_KEY = "clicksByCountry.language";

/** Simple `{placeholder}` find/replace - not full ICU MessageFormat, no pluralization rules. See types.ts. */
function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) => (key in params ? String(params[key]) : match));
}

/** Reads localStorage defensively - private browsing / disabled storage should degrade, not throw. */
function readStoredLanguage(): LanguageCode | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && isSupportedLanguage(stored) ? stored : null;
  } catch {
    return null;
  }
}

function writeStoredLanguage(language: LanguageCode): void {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Storage unavailable - the choice just won't survive a reload; not fatal.
  }
}

/** Matches navigator.language/languages (e.g. "sl-SI", "pt-BR") against our supported base codes. */
function languageFromBrowser(): LanguageCode | null {
  if (typeof navigator === "undefined") return null;
  const candidates = [...(navigator.languages ?? []), navigator.language].filter(Boolean);
  for (const candidate of candidates) {
    const base = candidate.split("-")[0].toLowerCase();
    if (isSupportedLanguage(base)) return base;
  }
  return null;
}

interface LanguageContextValue {
  language: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

/**
 * Owns the active UI language for the whole app - see README, "Podpora za
 * več jezikov" for the full picture. Three-step resolution, in priority
 * order:
 *
 *   1. A previously stored choice (localStorage) - "vedno odpre v jeziku,
 *      ki je bil nazadnje odprt": ANY earlier resolution, auto-detected or
 *      manually picked via the language switcher, always wins on every
 *      later visit. This is checked synchronously on mount (no flash of
 *      the wrong language before storage is read).
 *   2. First visit only (nothing stored yet): guess the visitor's country
 *      from their IP via /api/geo (a Cloudflare Pages Function - see
 *      functions/api/geo.ts and detectCountry.ts) and map it to a language
 *      (countryToLanguage.ts).
 *   3. If step 2 comes back empty (route unavailable, timeout, or an
 *      unmapped country) - fall back to the browser/OS language setting
 *      (navigator.language).
 *   4. If nothing matched a supported language at all - English.
 *
 * Whatever step 2-4 resolves to is immediately written to localStorage, so
 * detection only ever runs once per visitor.
 */
export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(() => readStoredLanguage() ?? DEFAULT_LANGUAGE);

  const setLanguage = useCallback((next: LanguageCode) => {
    setLanguageState(next);
    writeStoredLanguage(next);
  }, []);

  useEffect(() => {
    if (readStoredLanguage() !== null) return; // already resolved on an earlier visit - never re-detect

    let cancelled = false;
    (async () => {
      const country = await detectCountryFromIp();
      const fromCountry = country ? COUNTRY_TO_LANGUAGE[country.toUpperCase()] : undefined;
      if (cancelled) return;
      if (fromCountry) {
        setLanguage(fromCountry);
        return;
      }
      setLanguage(languageFromBrowser() ?? DEFAULT_LANGUAGE);
    })();

    return () => {
      cancelled = true;
    };
    // Deliberately runs once on mount only (guarded by the readStoredLanguage()
    // check above, not by a dependency) - detection must never re-run just
    // because `setLanguage` was called, which would be a self-triggering loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = isRtl(language) ? "rtl" : "ltr";
  }, [language]);

  const t = useCallback(
    (key: TranslationKey, params?: Record<string, string | number>): string => {
      const dict = translations[language] ?? translations[DEFAULT_LANGUAGE];
      const template = dict[key] ?? translations[DEFAULT_LANGUAGE][key] ?? key;
      return interpolate(template, params);
    },
    [language],
  );

  // Keeps the browser tab title/description in the active language too -
  // index.html's static <title>/<meta> are just the pre-JS/SEO default.
  useEffect(() => {
    document.title = t("meta.title");
    const descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) descriptionTag.setAttribute("content", t("meta.description"));
  }, [t]);

  const value = useMemo<LanguageContextValue>(() => ({ language, setLanguage, t }), [language, setLanguage, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within a LanguageProvider");
  return ctx;
}
